// Who may do what inside a project (SPEC-v3 2.2). The server always checks
// again; the web uses this to hide or disable controls and to say why, and
// the support assistant's why_not tool uses it to explain (docs/v3/ASSISTANT.md 3).
import type { OrgRole, ProjectAccessRole } from "./enums";

/** A person's access to a project (named ProjectRole in the web). */
export type PermissionRole = ProjectAccessRole;

/** Actions from the SPEC-v3 2.2 permission matrix. */
export type Action =
  | "read"
  | "edit" // activities, events, experts, stakeholders, ToR drafts, expenses...
  | "lead" // record ToR approval/changes, report submit/decision, invoices, amendments, contract, close, funder links
  | "admin" // privacy, data requests, join requests
  | "import" // stakeholder import, running-project import
  | "createProject"
  | "partners" // project partners and partner access
  | "log"; // activity log

const STAFF_EDIT: PermissionRole[] = ["owner", "admin", "lead", "member"];

export function can(role: PermissionRole | null | undefined, action: Action, opts: { isLead?: boolean } = {}): boolean {
  if (!role) return false;
  switch (action) {
    case "read":
      return true;
    case "edit":
    case "import":
      return STAFF_EDIT.includes(role);
    case "lead":
      return role === "lead" || opts.isLead === true;
    case "admin":
      return role === "owner" || role === "admin";
    case "partners":
      return role === "owner" || role === "admin" || role === "lead" || opts.isLead === true;
    case "log":
      return role !== "partner";
    case "createProject":
      return STAFF_EDIT.includes(role);
  }
}

/** Org-level: team pages, organisation settings. */
export function canOrg(role: OrgRole | null | undefined, action: "read" | "edit" | "invite" | "joinRequests"): boolean {
  if (!role) return false;
  if (action === "read") return true;
  return role === "owner" || role === "admin";
}

/** The sentence under a disabled control ("why can't I?"). */
export function whyNot(action: Action): string {
  switch (action) {
    case "lead":
      return "Only the contract lead can do this.";
    case "admin":
      return "Only organisation owners and admins can do this.";
    case "partners":
      return "Only the contract lead or an admin can change partner access.";
    default:
      return "Your role can read this page but not change it.";
  }
}

/** Every action, for the assistant's why_not tool. */
export const ACTIONS = ["read", "edit", "lead", "admin", "import", "createProject", "partners", "log"] as const satisfies readonly Action[];

/** What each action covers, in plain words. */
export const ACTION_LABELS: Record<Action, string> = {
  read: "Read this project",
  edit: "Edit activities, events, experts, stakeholders, ToR drafts and expenses",
  lead: "Record the funder's ToR decisions, submit reports, invoices and amendments, close the project",
  admin: "Privacy, data requests and join requests",
  import: "Import stakeholders or a running project",
  createProject: "Create a project",
  partners: "Manage project partners and their access",
  log: "See the activity log",
};

// ---------------------------------------------------------------------------
// Permissions per team member (plan 2026-11-03 fine-grained access).
// A contract lead or admin can withhold these from a member or viewer of one
// project. Nothing is withheld by default, so a new row means today's rights.
// Owners and admins are never limited; partners go by their partner grant.
// ---------------------------------------------------------------------------

export const PROJECT_PERMISSIONS = [
  "seeMoney", // budget, expenses, invoices, payment schedule, financial report, amendments, money on Home
  "handleMoney", // add, approve and send back expenses, receipts, invoices, budget changes
  "seePersonal", // email, phone and address of stakeholders, guests and experts
  // Areas: limit changing, not seeing.
  "activities",
  "events",
  "experts",
  "stakeholders",
  "reporting",
] as const;
export type ProjectPermission = (typeof PROJECT_PERMISSIONS)[number];

/** Ready-made sets of withheld permissions; any other combination is "custom". */
export const PERMISSION_PRESETS = {
  full: [],
  noMoney: ["seeMoney", "handleMoney"],
  noPersonal: ["seePersonal"],
  finance: ["activities", "events", "experts", "stakeholders"],
  events: ["handleMoney", "activities", "experts", "reporting"],
} as const satisfies Record<string, readonly ProjectPermission[]>;
export type PermissionPreset = keyof typeof PERMISSION_PRESETS;

export const PERMISSION_PRESET_LABELS: Record<PermissionPreset | "custom", string> = {
  full: "Full access",
  noMoney: "No money",
  noPersonal: "No personal details",
  finance: "Finance",
  events: "Events",
  custom: "Custom",
};

export const PERMISSION_LABELS: Record<ProjectPermission, { label: string; help: string }> = {
  seeMoney: { label: "See money", help: "Budget, expenses, invoices, payments and amendments" },
  handleMoney: { label: "Handle money", help: "Add and approve expenses, invoices and budget changes" },
  seePersonal: { label: "See personal details", help: "Email, phone and address of stakeholders, guests and experts" },
  activities: { label: "Change activities", help: "Activities, missions, meetings and deliverables" },
  events: { label: "Change events", help: "Events, guests, check-in and follow-up" },
  experts: { label: "Change experts", help: "Expert pages, assignments and timesheets" },
  stakeholders: { label: "Change stakeholders", help: "People, organisations, lists and imports" },
  reporting: { label: "Change reporting", help: "ToRs, logframe and reports" },
};

const WHY_NOT: Record<ProjectPermission, string> = {
  seeMoney: "You can't see this project's money.",
  handleMoney: "You can't handle this project's money.",
  seePersonal: "You can't see personal details on this project.",
  activities: "You can't change activities on this project.",
  events: "You can't change events on this project.",
  experts: "You can't change experts on this project.",
  stakeholders: "You can't change stakeholders on this project.",
  reporting: "You can't change reporting on this project.",
};

/** The sentence under a control a withheld permission turns off. */
export function whyNotPermission(perm: ProjectPermission): string {
  return `${WHY_NOT[perm]} Ask the contract lead.`;
}

/** Known names only, once each, in PROJECT_PERMISSIONS order; no seeing money means no handling it. */
export function normaliseWithheld(list: readonly string[]): ProjectPermission[] {
  const set = new Set(list);
  if (set.has("seeMoney")) set.add("handleMoney");
  return PROJECT_PERMISSIONS.filter((p) => set.has(p));
}

/** Which ready-made set a withheld list is, or "custom". */
export function presetOf(withheld: readonly ProjectPermission[]): PermissionPreset | "custom" {
  const key = normaliseWithheld(withheld).join(",");
  for (const [name, list] of Object.entries(PERMISSION_PRESETS)) {
    if (normaliseWithheld(list).join(",") === key) return name as PermissionPreset;
  }
  return "custom";
}

/** What a permission check needs to know about the caller. */
export type AccessScope = { role: PermissionRole; isLead?: boolean; withheld?: readonly ProjectPermission[] };

export function hasPermission(scope: AccessScope, perm: ProjectPermission): boolean {
  if (scope.role === "owner" || scope.role === "admin") return true;
  if (scope.role === "partner") return false;
  if (perm === "handleMoney" && scope.role === "viewer") return false;
  return !(scope.withheld ?? []).includes(perm);
}
